import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getAttribution } from "@/lib/track";
import { memberApi, getMemberToken, clearMember } from "@/lib/member";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const LeadContext = createContext(null);
export const useLead = () => useContext(LeadContext);

const TITLES = {
  tour: { title: "Book a Tour", desc: "Schedule a guided visit to a CoworkZen centre." },
  quote: { title: "Get a Quote", desc: "Tell us your needs for a customised plan." },
  enterprise: { title: "Talk to Our Team", desc: "Let's design the right workspace for your business." },
  general: { title: "Get in Touch", desc: "We'll get back to you shortly." },
};
const SPACE_TYPES = ["Coworking", "BTS", "Managed Office", "Meeting Room", "Training Room"];
const CITIES = ["Noida", "Greater Noida", "Delhi", "Gurugram", "Ghaziabad", "Faridabad", "Other"];

export function LeadProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("tour");
  const [prefill, setPrefill] = useState({});
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", space_type: "", city: "" });
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/public/lead-forms").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const openLead = async (t = "tour", pf = {}) => {
    setType(t);
    api.post(`/public/lead-forms/${t}/view`).catch(() => {});
    setPrefill(pf);
    setMember(null);
    setForm({ name: "", email: "", phone: "", company: "", message: "", space_type: pf.space_type || "", city: pf.city || "" });
    setOpen(true);
    if (getMemberToken()) {
      try {
        const { data } = await memberApi.me();
        setMember(data);
        setForm((f) => ({ ...f, name: data.name || "", email: data.email || "", phone: data.phone || "" }));
      } catch {
        clearMember();
        setMember(null);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    const sff = ((cfg && cfg[type]) || {}).fields || {};
    if (sff.phone !== false && sff.phone_required && !form.phone.trim()) return toast.error("Please add your phone number.");
    setBusy(true);
    try {
      await api.post("/public/leads", {
        ...form,
        email: member?.email || form.email,
        space_type: form.space_type,
        type,
        location_slug: prefill.location || "",
        workspace_slug: prefill.workspace || "",
        member_id: member?.id || "",
        ...getAttribution(),
      });
      toast.success("Thanks! Our team will reach out soon.");
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const fallback = TITLES[type] || TITLES.general;
  const fc = (cfg && cfg[type]) || {};
  const meta = { title: fc.title || fallback.title, desc: fc.description ?? fallback.desc, submit: fc.submit_label || fc.title || fallback.title };
  const spaceTypes = (fc.space_types && fc.space_types.length) ? fc.space_types : SPACE_TYPES;
  const cityList = (fc.cities && fc.cities.length) ? fc.cities : CITIES;
  const ff = fc.fields || {};
  const showPhone = ff.phone !== false, showCompany = ff.company !== false, showMessage = ff.message !== false;
  const phoneRequired = showPhone && ff.phone_required === true;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <LeadContext.Provider value={{ openLead }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{meta.title}</DialogTitle>
            <DialogDescription>{meta.desc}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1.5">Space type</p>
              <div className="flex flex-wrap gap-2" data-testid="lead-space-types">
                {spaceTypes.map((st) => {
                  const active = form.space_type === st;
                  return (
                    <button
                      type="button"
                      key={st}
                      data-testid={`lead-space-${st.toLowerCase().replace(/ /g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, space_type: st }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                    >
                      {st}
                    </button>
                  );
                })}
              </div>
            </div>
            <Input data-testid="lead-name" placeholder="Full name*" value={form.name} onChange={set("name")} />
            <Input data-testid="lead-email" type="email" placeholder="Work email*" value={form.email}
              onChange={set("email")} readOnly={!!member} className={member ? "bg-slate-50 text-slate-500" : ""} />
            {(showPhone || showCompany) && (
              <div className={`grid gap-3 ${showPhone && showCompany ? "grid-cols-2" : "grid-cols-1"}`}>
                {showPhone && <Input data-testid="lead-phone" placeholder={phoneRequired ? "Phone*" : "Phone"} value={form.phone} onChange={set("phone")} />}
                {showCompany && <Input data-testid="lead-company" placeholder="Company" value={form.company} onChange={set("company")} />}
              </div>
            )}
            <select data-testid="lead-city" value={form.city} onChange={set("city")}
              className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${form.city ? "text-slate-900" : "text-slate-400"}`}>
              <option value="" className="text-slate-900">Select city</option>
              {cityList.map((c) => <option key={c} value={c} className="text-slate-900">{c}</option>)}
            </select>
            {showMessage && <Textarea data-testid="lead-message" placeholder="Tell us more (optional)" value={form.message} onChange={set("message")} />}
            <Button data-testid="lead-submit" type="submit" disabled={busy} className="w-full rounded-full h-11">
              {busy ? "Sending..." : meta.submit}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </LeadContext.Provider>
  );
}
